import { of } from 'rxjs';
import { Component, OnDestroy, OnInit } from "@angular/core";
import { Location } from "@angular/common";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { Subject, Subscription } from "rxjs";
import { debounceTime, distinctUntilChanged } from "rxjs/operators";
import { FootballerFriend } from "../../shared/models/player";
import { PlayerService } from "../../shared/services/player.service";
import { navigateBackOrFallback, ScreenInitialLoading } from "../../shared/screen-shell/screen-navigation.utils";

type FootballerFriendsSortBy = "matchesTogether" | "winRate";
type FootballerFriendsSortDirection = "asc" | "desc";

@Component({
  selector: "app-player-footballer-friends",
  templateUrl: "./player-footballer-friends.component.html",
  styleUrls: ["../player.component.css"],
  standalone: false
})
export class PlayerFootballerFriendsComponent implements OnInit, OnDestroy {

  public friends: FootballerFriend[] = [];
  public loading = false;
  public errorMsg: string;
  public page = 0;
  public pageSize = 20;
  public hasMore = false;
  public total = 0;
  public searchQuery = "";
  public sortBy: FootballerFriendsSortBy = "matchesTogether";
  public sortDirection: FootballerFriendsSortDirection = "desc";
  public showInitialLoading = false;
  private returnToPlayerDetail = false;
  private returnPlayerId: string;
  private returnEventId: string;
  private readonly initialLoading = new ScreenInitialLoading(300, (visible) => this.showInitialLoading = visible);

  private readonly search$ = new Subject<string>();
  private searchSubscription: Subscription;

  constructor(
    private playerService: PlayerService,
    private translate: TranslateService,
    private route: ActivatedRoute,
    private router: Router,
    private location: Location
  ) {}

  public ngOnInit(): void {
    this.returnToPlayerDetail = this.route.snapshot.queryParamMap.get("returnToPlayerDetail") === "true";
    this.returnPlayerId = this.route.snapshot.queryParamMap.get("returnPlayerId");
    this.returnEventId = this.route.snapshot.queryParamMap.get("returnEventId");
    this.searchSubscription = this.search$.pipe(
      debounceTime(250),
      distinctUntilChanged()
    ).subscribe((query) => {
      this.searchQuery = query || "";
      this.loadPage(0);
    });
    this.loadPage(0);
  }

  public ngOnDestroy(): void {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
    this.initialLoading.destroy();
  }

  public goBack(): void {
    this.prepareReturnToPlayerDetail();
    navigateBackOrFallback(this.location, this.router, ["/player-detail", false]);
  }

  public onSearch(value: string): void {
    this.search$.next(value || "");
  }

  public clearSearch(): void {
    this.searchQuery = "";
    this.search$.next("");
  }

  public setSort(sortBy: FootballerFriendsSortBy): void {
    if (this.loading) {
      return;
    }
    if (this.sortBy === sortBy) {
      this.sortDirection = this.sortDirection === "desc" ? "asc" : "desc";
    } else {
      this.sortBy = sortBy;
      this.sortDirection = "desc";
    }
    this.loadPage(0);
  }

  public sortTitle(sortBy: FootballerFriendsSortBy): string {
    const direction = this.sortBy === sortBy ? this.sortDirection : "desc";
    const key = sortBy === "winRate"
      ? (direction === "asc" ? "players.sortWinRateAsc" : "players.sortWinRateDesc")
      : (direction === "asc" ? "players.sortMatchesTogetherAsc" : "players.sortMatchesTogetherDesc");
    return this.translate.instant(key);
  }

  public loadMore(): void {
    if (this.loading || !this.hasMore) {
      return;
    }
    this.loadPage(this.page + 1);
  }

  public isContentLoading(): boolean {
    return this.showInitialLoading && this.friends.length === 0;
  }

  public openMatchHistory(friend: FootballerFriend): void {
    if (!friend || !friend.playerId) {
      return;
    }
    this.router.navigate(["/player", friend.playerId, "match-history"], {
      queryParams: this.returnQueryParams()
    });
  }

  public displayName(friend: FootballerFriend): string {
    return friend ? (friend.nickName || friend.name || "-") : "-";
  }

  public initials(friend: FootballerFriend): string {
    const name = this.displayName(friend);
    return name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part.charAt(0).toUpperCase()).join("") || "?";
  }

  public lastSharedMatchLabel(friend: FootballerFriend): string {
    if (!friend || !friend.lastSharedMatchAt) {
      return "-";
    }
    const date = new Date(friend.lastSharedMatchAt);
    const locale = this.translate.currentLang === "hr" ? "hr-HR" : "en-US";
    return new Intl.DateTimeFormat(locale, {
      day: "numeric",
      month: "short",
      year: "numeric"
    }).format(date);
  }

  public winRate(friend: FootballerFriend): number {
    const decided = Number(friend && friend.togetherWins || 0) + Number(friend && friend.togetherDraws || 0) + Number(friend && friend.togetherLosses || 0);
    if (!decided) {
      return 0;
    }
    return Math.round((Number(friend.togetherWins || 0) / decided) * 100);
  }

  private initialPageConsumed = false;

  private loadPage(page: number): void {
    const prepared = !this.initialPageConsumed && this.route.snapshot.data['screen'];
    this.initialPageConsumed = true;
    this.beginLoading(page);
    this.errorMsg = null;
    (prepared ? of(prepared) : this.playerService.getFootballerFriendsStats(this.searchQuery, page, this.pageSize, this.sortBy, this.sortDirection)).subscribe(
      (response) => {
        this.page = response.page;
        this.total = response.total || 0;
        this.hasMore = response.hasMore;
        this.friends = page === 0 ? (response.items || []) : this.friends.concat(response.items || []);
        this.finishLoading();
      },
      () => {
        this.finishLoading();
        this.errorMsg = this.translate.instant("players.footballerFriendsLoadFailed");
      }
    );
  }

  private prepareReturnToPlayerDetail(): void {
    if (!this.returnToPlayerDetail || !this.returnPlayerId) {
      return;
    }
    localStorage.setItem("openPlayerDetailForPlayerId", this.returnPlayerId);
    if (this.returnEventId) {
      localStorage.setItem("openPlayerDetailForEventId", this.returnEventId);
    }
  }

  private returnQueryParams(): {[key: string]: string} {
    if (!this.returnToPlayerDetail || !this.returnPlayerId) {
      return {};
    }
    const queryParams: {[key: string]: string} = {
      returnToPlayerDetail: "true",
      returnPlayerId: this.returnPlayerId
    };
    if (this.returnEventId) {
      queryParams.returnEventId = this.returnEventId;
    }
    return queryParams;
  }

  private beginLoading(page: number): void {
    this.loading = true;
    if (page === 0) {
      this.hasMore = false;
    }
    this.initialLoading.begin(page === 0 && this.friends.length === 0, () => this.loading);
  }

  private finishLoading(): void {
    this.loading = false;
    this.initialLoading.finish();
  }
}
